/**
 * Sustainability-forecasting carbon kernel — category series, timing,
 * adders, move, targets (SIL-435, W2 of
 * resources/plans/sustainability-forecasting-spec.md §3-4, full formulas in
 * the SIL-424/SIL-428 grilling resolutions cited inline below).
 *
 * Same lineage as `scenario-use-phase.ts` (T8's single-cohort projection):
 * pure, no DB access, no IO, NEVER inside `scenarioEngine`'s dollar math
 * (`scenario-engine.ts`). This module is the multi-cohort, multi-action,
 * category-tagged extension that lineage needed for W2 — `buy` and `move`
 * are first-class actions here (spec §1) even though
 * `financial_scenario_cohort.action` (dto/financials.ts `scenarioActionSchema`)
 * does not carry them yet; wiring those into the DB-backed cohort enum is an
 * API-layer concern for a later ticket (spec §11 step 4), out of scope for
 * this pure-kernel W2 slice.
 *
 * ── W1 SCHEMA THIS MODULE TARGETS (already on main) ─────────────────────
 * `carbon_target` (718): scope_type 'org'|'team' ONLY — the spec's
 * `data_center` scope was dropped in review (708/718 precedent: a
 * customer-owned target cannot anchor to a seller-owned facility row). This
 * kernel's target resolution (`resolveCarbonTarget`) matches that: org|team
 * only, never re-introduce a facility scope.
 * `customer_org_sustainability_config` (719): org-level `operated` flag
 * (drives scope2-vs-cat8 tagging for EVERY cohort in an org, not per
 * facility), the three SIL-420 adder scalars, and the Cat1/2 purchase
 * toggle (`purchase_ghg_category` — this kernel does not need the toggle
 * itself; it tags every embodied-hardware lump `cat1_or_2` uniformly, and
 * the API/UI layer renders the org's chosen label per spec §4's category
 * list, "Category 1/2 · purchased hardware (embodied)").
 * `asset_class_unit_weight` (719): platform reference weight, TWO-tier
 * resolution only (platform default -> scenario override) — this kernel
 * takes the already-resolved `unitWeightKg` as a plain input; it does not
 * know or care which tier produced it.
 * `grid_intensity_factor` (720): the WOR ("no location") floor is 390.00
 * g/kWh at docker-2.3.0 (SIL-430's empirical correction — the older
 * research doc's 590.48 was wrong; trust 390.00). This kernel never reads
 * the table itself (no DB access) — callers resolve a country's
 * `gCo2ePerKwh` and pass it in as a plain number.
 *
 * ── DESIGN CONSTRAINT #2 (load-bearing) — the advisory firewall ─────────
 * The three advisory numbers (avoided-vs-new, disposal-deferred,
 * downstream-reuse — spec §5, SIL-424 #6 + SIL-426 fixes) are computed by
 * the functions in the "Advisory panel" section below, and are typed
 * `AdvisoryKgCo2e` — a WRAPPER OBJECT, not a `number`. `CategoryKgCo2ePoint`
 * fields are plain `number`s. Because `AdvisoryKgCo2e` is not a `number`
 * (not even a branded one — a branded number IS STILL a number and would
 * silently widen into a plain `number` field, which is exactly the leak
 * this boundary must prevent), an `AdvisoryKgCo2e` value can be neither
 * assigned into nor arithmetically summed into a `CategoryKgCo2ePoint`
 * field without an explicit, grep-able `.kgCo2e` unwrap — see the
 * `@ts-expect-error` tests in `scenario-carbon-kernel.test.ts`.
 */

import { USAGE_LOCATION_WOR_FLOOR } from './usage-location.js';

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Hours/year used for a hypothetical BUY's derived rate (spec §3: "buys =
 * watts x 8766h x PUE x grid_intensity_factor(target)"). Deliberately 8766,
 * NOT `USE_PHASE_HOURS_PER_YEAR` (8760, `use-phase-math.ts`) — the ticket's
 * literal formula uses the 365.25-day average-year figure for a forward
 * hypothetical projection, distinct from the asset-detail pipeline's
 * calendar-365 convention for an already-measured device. Kept as its own
 * named constant (not reused from use-phase-math.ts) so this deliberate
 * divergence is visible at the call site, not a silent typo.
 */
export const SCENARIO_CARBON_BUY_HOURS_PER_YEAR = 8766;

/**
 * DEFRA 2026 road HGV rigid >17t, laden average freight factor
 * (kgCO2e/tonne-km) — SIL-420 §(b) default, used for BOTH the used-buy
 * transport adder and the move Cat-4 freight lump (spec §3: "Move ... Cat-4
 * freight lump (transport scalars, no refurb)" — "transport scalars" means
 * this factor plus the org's `used_transport_km` scalar, reused verbatim
 * for a move rather than a geodesic source->target distance the kernel has
 * no way to compute — see this module's header, "no DB access, no IO").
 * Not an org-configurable scalar (unlike `used_transport_km`/
 * `disposal_weee_factor`/`used_refurb_pct` on `customer_org_sustainability_
 * config`, 719) — resources/plans/sil-420-disposal-and-used-adder-defaults.md
 * §(b) lists it as a platform default with sea/air overrides "as overrides
 * for intercontinental moves", not a stored per-org column in 719's shipped
 * shape.
 */
export const CARBON_KERNEL_FREIGHT_FACTOR_KGCO2E_PER_TONNE_KM = 0.123;

// ---------------------------------------------------------------------------
// 1. Category series — the official footprint, co-indexed by month
// ---------------------------------------------------------------------------

/** The five GHG-category buckets a carbon series point may carry (spec §3,
 * disclosure copy §4). Never a sixth "advisory" bucket — see this module's
 * header. */
export type GhgCategoryKey = 'scope2' | 'cat8' | 'cat1_or_2' | 'cat5' | 'cat4';

/** One month's (or one cohort's) kgCO2e broken out by category. Every field
 * is a plain `number` — the structural half of the advisory firewall (see
 * module header): nothing that produces an `AdvisoryKgCo2e` can satisfy this
 * shape without an explicit `.kgCo2e` unwrap. */
export interface CategoryKgCo2ePoint {
  scope2: number;
  cat8: number;
  cat1_or_2: number;
  cat5: number;
  cat4: number;
}

export const ZERO_CATEGORY_POINT: Readonly<CategoryKgCo2ePoint> = Object.freeze({
  scope2: 0,
  cat8: 0,
  cat1_or_2: 0,
  cat5: 0,
  cat4: 0,
});

/** @pure */
export function addCategoryPoints(a: CategoryKgCo2ePoint, b: CategoryKgCo2ePoint): CategoryKgCo2ePoint {
  return {
    scope2: a.scope2 + b.scope2,
    cat8: a.cat8 + b.cat8,
    cat1_or_2: a.cat1_or_2 + b.cat1_or_2,
    cat5: a.cat5 + b.cat5,
    cat4: a.cat4 + b.cat4,
  };
}

/** @pure */
export function subtractCategoryPoints(a: CategoryKgCo2ePoint, b: CategoryKgCo2ePoint): CategoryKgCo2ePoint {
  return {
    scope2: round3(a.scope2 - b.scope2),
    cat8: round3(a.cat8 - b.cat8),
    cat1_or_2: round3(a.cat1_or_2 - b.cat1_or_2),
    cat5: round3(a.cat5 - b.cat5),
    cat4: round3(a.cat4 - b.cat4),
  };
}

/** Which category an operate-rate leg tags into — org-level `operated`
 * (`customer_org_sustainability_config.operated`, 719) drives this for
 * EVERY cohort in the org; there is no per-facility rung in v1 (see module
 * header). @pure */
export function operateCategoryKey(operated: boolean): Extract<GhgCategoryKey, 'scope2' | 'cat8'> {
  return operated ? 'scope2' : 'cat8';
}

// ---------------------------------------------------------------------------
// 2. Rate sources (spec §3: "Rate source")
// ---------------------------------------------------------------------------

export type CarbonRateSource = 'owned_snapshot' | 'buy_derived';

/**
 * Hypothetical-buy (or post-move / post-refresh-new-fleet) derived annual
 * rate: `watts x 8766h x PUE x grid_intensity_factor(target)` (spec §3).
 * `watts`/rate are already cohort-aggregate figures (same "cohort/class
 * granularity" convention `scenario-engine.ts` uses for `totalWatts`) — this
 * function does not multiply by a unit count.
 *
 * @pure
 */
export function resolveBuyDerivedAnnualRateKgco2ePerYr(
  watts: number,
  pue: number,
  gridIntensityGPerKwh: number,
): number {
  const kwhPerYear = (watts / 1000) * SCENARIO_CARBON_BUY_HOURS_PER_YEAR;
  const gramsPerYear = kwhPerYear * pue * gridIntensityGPerKwh;
  return round3(gramsPerYear / 1000);
}

// ---------------------------------------------------------------------------
// 3. Adders (spec §3, SIL-420 defaults)
// ---------------------------------------------------------------------------

/**
 * Cat-5 disposal adder: `unit_weight_kg / 1000 x weee_factor`. `null` weight
 * (asset_class_unit_weight's `cloud_vm` row, 719) is a true zero — "no
 * physical disposal applies" — never a gap.
 *
 * @pure
 */
export function disposalAdderKgco2e(unitWeightKg: number | null, weeeFactorKgco2ePerTonne: number): number {
  if (unitWeightKg === null) return 0;
  return round3((unitWeightKg / 1000) * weeeFactorKgco2ePerTonne);
}

/**
 * Transport component shared by BOTH the used-buy adder and the move Cat-4
 * freight lump: `unit_weight_kg / 1000 x transport_km x freight_factor`
 * (SIL-420 §(b); move reuses this verbatim per spec §3 — see
 * `CARBON_KERNEL_FREIGHT_FACTOR_KGCO2E_PER_TONNE_KM`'s docstring).
 *
 * @pure
 */
export function transportAdderKgco2e(
  unitWeightKg: number | null,
  transportKm: number,
  freightFactorKgco2ePerTonneKm: number = CARBON_KERNEL_FREIGHT_FACTOR_KGCO2E_PER_TONNE_KM,
): number {
  if (unitWeightKg === null) return 0;
  return round3((unitWeightKg / 1000) * transportKm * freightFactorKgco2ePerTonneKm);
}

/**
 * Refurbishment component of the used-buy adder: `refurb_pct x
 * sku_embodied_kgco2e` (SIL-420 §(b)).
 *
 * @pure
 */
export function refurbAdderKgco2e(refurbPct: number, skuEmbodiedKgco2e: number): number {
  return round3(refurbPct * skuEmbodiedKgco2e);
}

// ---------------------------------------------------------------------------
// 4. Cohort inputs — hold | sell | itad | refresh | buy | move
// ---------------------------------------------------------------------------

export type CarbonScenarioCohortAction = 'hold' | 'sell' | 'itad' | 'refresh' | 'buy' | 'move';

interface CarbonCohortBase {
  label: string;
  /** customer_org_sustainability_config.operated for this cohort's org —
   * see `operateCategoryKey`. */
  operated: boolean;
}

/** A hypothetical (or new-fleet-of-a-refresh) hardware acquisition — shared
 * by `BuyCarbonCohort` and `RefreshCarbonCohort.newFleet`. */
export interface CarbonBuyLikeInputs {
  /** 'new' — full SKU embodied carbon lumps once, in the purchase month
   * (Cat 1/2), spec §3 "Buy-new embodied". 'used' — ~0 embodied, replaced by
   * the transport+refurb adder (Cat 4), spec §3 "Buy-used embodied
   * convention". */
  condition: 'new' | 'used';
  /** Cohort-aggregate wattage draw for the buy-derived rate. */
  watts: number;
  pue: number;
  /** Grid factor at the BUY's (target) location — spec §3 "target
   * location". */
  gridIntensityGPerKwh: number;
  /** Cohort-aggregate SKU embodied carbon — the new-buy embodied lump
   * directly, or the refurb adder's base for a used buy. */
  skuEmbodiedKgco2e: number;
  /** Only consulted for a 'used' buy (transport adder input). */
  unitWeightKg: number | null;
  /** Org scalar (`used_transport_km`, possibly scenario-overridden). */
  transportKm: number;
  /** Org scalar (`used_refurb_pct`, possibly scenario-overridden). */
  refurbPct: number;
}

export interface HoldCarbonCohort extends CarbonCohortBase {
  action: 'hold';
  /** Cohort-aggregate device-level annual rate (kgCO2e/yr) read off
   * `asset_use_phase_snapshot` — hold ignores timing entirely (spec §3). */
  currentAnnualRateKgco2ePerYr: number;
}

export interface SellCarbonCohort extends CarbonCohortBase {
  action: 'sell';
  timingMonths: number;
  currentAnnualRateKgco2ePerYr: number;
}

export interface ItadCarbonCohort extends CarbonCohortBase {
  action: 'itad';
  timingMonths: number;
  currentAnnualRateKgco2ePerYr: number;
  unitWeightKg: number | null;
  weeeFactorKgco2ePerTonne: number;
}

export interface RefreshCarbonCohort extends CarbonCohortBase {
  action: 'refresh';
  timingMonths: number;
  /** Old fleet's rate, pre-refresh. */
  currentAnnualRateKgco2ePerYr: number;
  /** How the OLD leg is retired at `timingMonths` — spec §3 "refresh =
   * old-leg (sell|dispose per cohort, default sell) + new-fleet start at
   * m"; the API layer resolves the per-cohort default before calling in
   * (this kernel takes the already-resolved choice, never defaults it
   * itself). */
  oldLegDisposal: 'sell' | 'itad';
  /** Only consulted when `oldLegDisposal === 'itad'`. */
  unitWeightKg: number | null;
  weeeFactorKgco2ePerTonne: number;
  /** New fleet, modeled exactly like a `buy` starting at `timingMonths`
   * (spec §3 "new-fleet start at m") — this is what resolves the gap the
   * W1-era kernel (`scenario-use-phase.ts`'s `hasUnmodeledPostRefreshRemainder`)
   * flagged as unmodeled: a refresh's replacement fleet now gets an honest
   * buy-derived rate instead of no rate at all. */
  newFleet: CarbonBuyLikeInputs;
}

export interface BuyCarbonCohort extends CarbonCohortBase {
  action: 'buy';
  timingMonths: number;
  buy: CarbonBuyLikeInputs;
}

export interface MoveCarbonCohort extends CarbonCohortBase {
  action: 'move';
  timingMonths: number;
  /** Rate BEFORE the move (source location, owned snapshot). */
  currentAnnualRateKgco2ePerYr: number;
  /** Cohort-aggregate wattage, used to re-derive the rate at the TARGET
   * location post-move (spec §3 "rate switches source->target at m"; there
   * is no snapshot yet for the asset at its new facility, so the post-move
   * leg uses the same buy-derived formula as a hypothetical buy). */
  watts: number;
  pue: number;
  targetGridIntensityGPerKwh: number;
  /** Boavizta trigram for the target location — validated against the
   * no-location floor below (spec §3 "moves to 'No location' disallowed").
   */
  targetUsageLocation: string;
  /** Cat-4 freight lump inputs — transport scalars only, no refurb (spec
   * §3). */
  unitWeightKg: number | null;
  transportKm: number;
}

export type CarbonScenarioCohort =
  | HoldCarbonCohort
  | SellCarbonCohort
  | ItadCarbonCohort
  | RefreshCarbonCohort
  | BuyCarbonCohort
  | MoveCarbonCohort;

/**
 * Validates a move cohort's target location is a REAL location, never the
 * WOR no-location floor (spec §3 "moves to 'No location' disallowed").
 * Thrown, not silently clamped — same fail-loud posture as `applyPue`'s
 * null-PUE guard (`use-phase-math.ts`): a caller that reaches this function
 * with an unvalidated 'No location' target has skipped a check the route
 * layer must perform BEFORE ever invoking the kernel.
 *
 * @pure
 * @throws {Error} if `targetUsageLocation` is the WOR floor.
 */
export function assertMoveTargetLocationAllowed(targetUsageLocation: string): void {
  if (targetUsageLocation === USAGE_LOCATION_WOR_FLOOR) {
    throw new Error(
      `scenario-carbon-kernel: a 'move' action's target location cannot be ` +
        `'${USAGE_LOCATION_WOR_FLOOR}' (no location) — moves to "No location" ` +
        'are disallowed (spec §3); resolve a real target location before calling.',
    );
  }
}

// ---------------------------------------------------------------------------
// 5. Timing model — whole-month, cumulative-to-date per point (mirrors
//    scenarioEngine's own ScenarioCompareSeriesPoint convention, so a lump
//    lands once and then stays part of the running total exactly the way
//    `recoveredValueUsd` does in scenario-engine.ts's scenarioCohortAt).
// ---------------------------------------------------------------------------

function operateCumulativeKgco2e(annualRateKgco2ePerYr: number, months: number): number {
  return round3((annualRateKgco2ePerYr * months) / 12);
}

function pointWith(key: GhgCategoryKey, value: number): CategoryKgCo2ePoint {
  return { ...ZERO_CATEGORY_POINT, [key]: value };
}

/**
 * A buy-like acquisition's cumulative contribution as of `month`: operate
 * (from `startMonth` onward, buy-derived rate) plus the one-time embodied
 * (new) or transport+refurb (used) lump landing at `startMonth` and
 * persisting thereafter.
 */
function buyLikeContributionAt(
  buy: CarbonBuyLikeInputs,
  month: number,
  startMonth: number,
  horizonMonths: number,
  categoryKey: Extract<GhgCategoryKey, 'scope2' | 'cat8'>,
): CategoryKgCo2ePoint {
  let point: CategoryKgCo2ePoint = { ...ZERO_CATEGORY_POINT };

  const operateMonths = Math.max(0, Math.min(month, horizonMonths) - startMonth);
  if (operateMonths > 0) {
    const annualRate = resolveBuyDerivedAnnualRateKgco2ePerYr(buy.watts, buy.pue, buy.gridIntensityGPerKwh);
    point = addCategoryPoints(point, pointWith(categoryKey, operateCumulativeKgco2e(annualRate, operateMonths)));
  }

  if (month >= startMonth) {
    if (buy.condition === 'new') {
      point = addCategoryPoints(point, pointWith('cat1_or_2', round3(buy.skuEmbodiedKgco2e)));
    } else {
      const transport = transportAdderKgco2e(buy.unitWeightKg, buy.transportKm);
      const refurb = refurbAdderKgco2e(buy.refurbPct, buy.skuEmbodiedKgco2e);
      point = addCategoryPoints(point, pointWith('cat4', round3(transport + refurb)));
    }
  }

  return point;
}

/** Baseline = hold whatever this cohort currently owns, untouched by the
 * scenario action (mirrors `scenarioEngine`'s `baselineCohortAt` — same
 * cohort-owned figures held for the full horizon). A hypothetical `buy`
 * cohort owns nothing today, so its baseline is a true zero (nothing to
 * hold), not a gap. */
function baselineCohortAt(cohort: CarbonScenarioCohort, month: number): CategoryKgco2ePointAlias {
  if (cohort.action === 'buy') return { ...ZERO_CATEGORY_POINT };

  const key = operateCategoryKey(cohort.operated);
  return pointWith(key, operateCumulativeKgco2e(cohort.currentAnnualRateKgco2ePerYr, month));
}

type CategoryKgco2ePointAlias = CategoryKgCo2ePoint;

/** Scenario = the cohort's action applied, per spec §3's per-action rules. */
function scenarioCohortAt(
  cohort: CarbonScenarioCohort,
  month: number,
  horizonMonths: number,
): CategoryKgCo2ePoint {
  const key = operateCategoryKey(cohort.operated);

  switch (cohort.action) {
    case 'hold':
      return pointWith(key, operateCumulativeKgco2e(cohort.currentAnnualRateKgco2ePerYr, month));

    case 'sell': {
      const operateMonths = Math.min(month, cohort.timingMonths);
      return pointWith(key, operateCumulativeKgco2e(cohort.currentAnnualRateKgco2ePerYr, operateMonths));
    }

    case 'itad': {
      const operateMonths = Math.min(month, cohort.timingMonths);
      let point = pointWith(key, operateCumulativeKgco2e(cohort.currentAnnualRateKgco2ePerYr, operateMonths));
      if (month >= cohort.timingMonths) {
        const disposal = disposalAdderKgco2e(cohort.unitWeightKg, cohort.weeeFactorKgco2ePerTonne);
        point = addCategoryPoints(point, pointWith('cat5', disposal));
      }
      return point;
    }

    case 'refresh': {
      const oldOperateMonths = Math.min(month, cohort.timingMonths);
      let point = pointWith(key, operateCumulativeKgco2e(cohort.currentAnnualRateKgco2ePerYr, oldOperateMonths));
      if (month >= cohort.timingMonths && cohort.oldLegDisposal === 'itad') {
        const disposal = disposalAdderKgco2e(cohort.unitWeightKg, cohort.weeeFactorKgco2ePerTonne);
        point = addCategoryPoints(point, pointWith('cat5', disposal));
      }
      const newFleetPoint = buyLikeContributionAt(cohort.newFleet, month, cohort.timingMonths, horizonMonths, key);
      return addCategoryPoints(point, newFleetPoint);
    }

    case 'buy':
      return buyLikeContributionAt(cohort.buy, month, cohort.timingMonths, horizonMonths, key);

    case 'move': {
      assertMoveTargetLocationAllowed(cohort.targetUsageLocation);
      const preMoveMonths = Math.min(month, cohort.timingMonths);
      let point = pointWith(key, operateCumulativeKgco2e(cohort.currentAnnualRateKgco2ePerYr, preMoveMonths));

      const postMoveMonths = Math.max(0, Math.min(month, horizonMonths) - cohort.timingMonths);
      if (postMoveMonths > 0) {
        const targetRate = resolveBuyDerivedAnnualRateKgco2ePerYr(
          cohort.watts,
          cohort.pue,
          cohort.targetGridIntensityGPerKwh,
        );
        point = addCategoryPoints(point, pointWith(key, operateCumulativeKgco2e(targetRate, postMoveMonths)));
      }

      if (month >= cohort.timingMonths) {
        const freight = transportAdderKgco2e(cohort.unitWeightKg, cohort.transportKm);
        point = addCategoryPoints(point, pointWith('cat4', freight));
      }
      return point;
    }
  }
}

// ---------------------------------------------------------------------------
// 6. Top-level series builder
// ---------------------------------------------------------------------------

export interface CarbonScenarioKernelInputs {
  /** 12 | 24 | 36 | 60 (same DB CHECK as `scenarioEngine`'s horizonMonths) —
   * not re-validated here; pure math accepts any non-negative horizon. */
  horizonMonths: number;
  cohorts: CarbonScenarioCohort[];
}

export interface CarbonScenarioSeriesPoint {
  /** 0..horizonMonths inclusive — SAME index range and SAME month values as
   * `scenarioEngine`'s `ScenarioCompareSeriesPoint[]`, so the API layer can
   * zip carbon[i] with dollars[i] without realignment (design constraint
   * #3). */
  month: number;
  baseline: CategoryKgCo2ePoint;
  scenario: CategoryKgCo2ePoint;
  delta: CategoryKgCo2ePoint;
}

/**
 * Builds the co-indexed monthly category series (spec §3 "Output contract")
 * — one point per month, 0..horizonMonths inclusive, each carrying
 * baseline/scenario/delta category maps. Cumulative-to-date per point
 * (mirrors `scenarioEngine`'s own convention exactly) — a lump lands once at
 * its action month and then persists in every later point's running total,
 * the same way `recoveredValueUsd` behaves in `scenario-engine.ts`.
 *
 * @pure
 */
export function buildCarbonScenarioSeries(inputs: CarbonScenarioKernelInputs): CarbonScenarioSeriesPoint[] {
  const { horizonMonths, cohorts } = inputs;
  const series: CarbonScenarioSeriesPoint[] = [];

  for (let month = 0; month <= horizonMonths; month += 1) {
    let baseline: CategoryKgCo2ePoint = { ...ZERO_CATEGORY_POINT };
    let scenario: CategoryKgCo2ePoint = { ...ZERO_CATEGORY_POINT };
    for (const cohort of cohorts) {
      baseline = addCategoryPoints(baseline, baselineCohortAt(cohort, month));
      scenario = addCategoryPoints(scenario, scenarioCohortAt(cohort, month, horizonMonths));
    }
    series.push({ month, baseline, scenario, delta: subtractCategoryPoints(scenario, baseline) });
  }

  return series;
}

// ---------------------------------------------------------------------------
// 7. Advisory panel (spec §5, SIL-424 #6 + SIL-426 fixes) — FIREWALLED.
//    `AdvisoryKgCo2e` is deliberately an OBJECT, not a (branded or plain)
//    number — see module header. Never exported alongside anything that
//    would let it satisfy a `CategoryKgCo2ePoint` field.
// ---------------------------------------------------------------------------

const ADVISORY_BRAND = Symbol('AdvisoryKgCo2e');

/** A firewalled advisory figure — structurally NOT a `number`, so it cannot
 * satisfy a `CategoryKgCo2ePoint` field (a plain `number`) by assignment,
 * and `+`/arithmetic against it is a compile error, not just a lint rule.
 * Unwrap explicitly via `.kgCo2e` (grep-able) if a caller ever needs the
 * raw figure — never for summing into the footprint. */
export interface AdvisoryKgCo2e {
  readonly [ADVISORY_BRAND]: true;
  readonly kgCo2e: number;
}

/** @pure */
export function advisoryKgCo2e(kgCo2e: number): AdvisoryKgCo2e {
  return { [ADVISORY_BRAND]: true, kgCo2e: round3(kgCo2e) };
}

/**
 * "Avoided vs new" (spec §5/§10 copy §10): baseline is the same SKUs bought
 * new (full embodied); the used purchase's own footprint is just the
 * transport+refurb adder; the difference is what buying used avoided vs
 * that hypothetical new-purchase baseline. Never negative (a used adder
 * larger than new embodied — implausible given the adder is a small
 * fraction of embodied, but clamped defensively same as this codebase's
 * other "never negative" adder conventions).
 *
 * @pure
 */
export function advisoryAvoidedVsNewKgCo2e(buy: CarbonBuyLikeInputs): AdvisoryKgCo2e {
  const transport = transportAdderKgco2e(buy.unitWeightKg, buy.transportKm);
  const refurb = refurbAdderKgco2e(buy.refurbPct, buy.skuEmbodiedKgco2e);
  const avoided = buy.skuEmbodiedKgco2e - (transport + refurb);
  return advisoryKgCo2e(Math.max(0, avoided));
}

/**
 * "Disposal deferred" (RENAMED per legal vet — deferred, not avoided, copy
 * §10): baseline is the same cohort routed to ITAD disposal now instead of
 * sale; selling defers exactly that disposal-adder quantity to a future
 * owner rather than eliminating it.
 *
 * @pure
 */
export function advisoryDisposalDeferredKgCo2e(
  unitWeightKg: number | null,
  weeeFactorKgco2ePerTonne: number,
): AdvisoryKgCo2e {
  return advisoryKgCo2e(disposalAdderKgco2e(unitWeightKg, weeeFactorKgco2ePerTonne));
}

/**
 * "Downstream reuse" (copy §10, shown by default): baseline is each sold
 * unit replaced by a newly manufactured equivalent downstream — the claim
 * is that replacement's full embodied carbon.
 *
 * @pure
 */
export function advisoryDownstreamReuseKgCo2e(skuEmbodiedKgco2e: number): AdvisoryKgCo2e {
  return advisoryKgCo2e(skuEmbodiedKgco2e);
}

// ---------------------------------------------------------------------------
// 8. Target resolution (spec §4, SIL-423 — carbon_target, 718)
// ---------------------------------------------------------------------------

/** Mirrors the `carbon_target` (718/722) row shape — org|team ONLY
 * (data_center scope dropped, see module header; never re-add a facility
 * scope here).
 *
 * SIL-474 (722): a row may now be `targetType='reduction_percent'` (org
 * scope only), which carries NO `tco2ePerYr` (null) — a chart/line renderer
 * consuming this type MUST branch on `targetType` (or, equivalently, on
 * `tco2ePerYr`'s nullness) rather than assume every resolved target has a
 * flat annual number to draw. See `resolveCarbonTarget`'s callers for the
 * "hide the line rather than render a false zero" convention. */
export interface CarbonTargetRow {
  id: string;
  customerOrgId: string;
  scopeType: 'org' | 'team';
  /** null for the org headline row; set for a team row. */
  teamId: string | null;
  /** 'absolute_budget' | 'reduction_percent' — see module comment above. */
  targetType: 'absolute_budget' | 'reduction_percent';
  /** Non-null only for `targetType='absolute_budget'`. */
  tco2ePerYr: number | null;
  baseYear: number | null;
  recalcPolicyNote: string | null;
}

/** The active filter scope a chart/export is currently drawn against (spec
 * §4: "chart draws the target matching the active filter scope"). */
export type CarbonTargetFilterScope =
  | { scopeType: 'org' }
  | { scopeType: 'team'; teamId: string };

/**
 * Resolves which (if any) `carbon_target` row matches the active filter
 * scope — org and team lenses are independent and NEVER summed (spec §4),
 * so this returns at most one row, never an aggregate across scopes.
 *
 * @pure
 */
export function resolveCarbonTarget(
  targets: readonly CarbonTargetRow[],
  scope: CarbonTargetFilterScope,
): CarbonTargetRow | null {
  if (scope.scopeType === 'org') {
    return targets.find((t) => t.scopeType === 'org') ?? null;
  }
  return targets.find((t) => t.scopeType === 'team' && t.teamId === scope.teamId) ?? null;
}

// ---------------------------------------------------------------------------
// 9. Basis envelope (spec Cross-cutting / SIL-426 §14) — rate source per
//    cohort, factor versions, scalars + overridden flags.
//
//    Deliberately NOT threaded through `dto/financials.ts`'s
//    `CalculationBasisInput`/`basisSourceSchema` here: that schema's
//    `BasisSource` enum is a closed allowlist ("Extend this enum ... when a
//    slice needs a source this list doesn't yet cover" — its own docstring),
//    and extending it is an API-layer wiring decision that belongs to the
//    ticket that actually assembles the `/scenarios/:id/compare` carbon
//    block response (spec §11 step 4), not this pure-kernel W2 slice. This
//    section's `describeCarbonKernelBasis` is a self-contained, pure
//    `describeX(inputs)` in the same spirit (spec Cross-cutting "Assembly
//    seam") that a future API-layer resolver can translate into
//    `CalculationBasisInput[]` once it extends that enum.
// ---------------------------------------------------------------------------

export interface CarbonKernelCohortBasisEntry {
  cohortLabel: string;
  rateSource: CarbonRateSource;
  /** Present only when `rateSource === 'buy_derived'` (or a move's
   * post-switch leg) — the grid_intensity_factor row's `source` +
   * `source_version` that produced the grid factor used. */
  gridIntensitySource?: string;
  gridIntensitySourceVersion?: string;
}

export interface CarbonKernelScalarBasisEntry {
  name: 'disposalWeeeFactor' | 'usedTransportKm' | 'usedRefurbPct' | 'freightFactor';
  value: number;
  /** true when this scenario supplied its own override rather than the
   * org (or platform, for freightFactor) default. */
  isOverridden: boolean;
}

export interface CarbonKernelBasis {
  cohorts: CarbonKernelCohortBasisEntry[];
  scalars: CarbonKernelScalarBasisEntry[];
}

/**
 * Pure `describeX(inputs)` beside the kernel it documents (spec
 * Cross-cutting "Assembly seam") — assembles the SIL-426 §14 methodology
 * sheet's rate-source-per-cohort and scalar+overridden-flag disclosures.
 *
 * @pure
 */
export function describeCarbonKernelBasis(params: {
  cohorts: CarbonKernelCohortBasisEntry[];
  scalars: CarbonKernelScalarBasisEntry[];
}): CarbonKernelBasis {
  return { cohorts: params.cohorts, scalars: params.scalars };
}
